import {
  OpenFeature,
  ProviderEvents,
  ProviderStatus,
  type Client,
  type EvaluationContext,
  type JsonValue,
  type Provider,
} from '@openfeature/web-sdk';
import type { FlagDefinition, FlagValueOf, JsonFlagValue } from '@ringba/config/flags';
import { deepEqual } from '@ringba/core/utils';
import { hashUserLogin } from '@ringba/telemetry/identity';
import { createManualPromise, type ManualPromise } from '@zajno/common/async/manualPromise';
import { Event, type IEvent } from '@zajno/common/observing/event';
import { logger } from './logger';
import { resolveTargetingKey } from './targetingKey';
import type { ISystemFlagsClient, ITargetingKeyStore, ResolvedSystemFlagsContext, SettleReason, SettleResult, SystemFlagsContext } from './types';

const EMPTY_CONTEXT: ResolvedSystemFlagsContext = { accountId: null, userDomain: null, userHash: null };

/** Lifecycle phase, distinct from the per-cycle settled state. */
type EngineState = 'idle' | 'started' | 'disposed';

/** What the current settle cycle can answer with. */
type CycleState = 'waiting' | 'ended-without-snapshot' | 'ended-with-snapshot';

/**
 * Reduces a login to the attributes evaluation targets on, so the address itself is never stored or sent.
 * Absent and `null` mean the same thing to the flag service, so comparisons only ever see the filled shape.
 */
function resolveContext(context: SystemFlagsContext): ResolvedSystemFlagsContext {
  const login = context.userLogin?.trim().toLowerCase() ?? null;
  const separator = login?.lastIndexOf('@') ?? -1;

  return {
    accountId: context.accountId ?? null,
    userDomain: separator < 0 ? null : login!.slice(separator + 1),
    userHash: hashUserLogin(login),
  };
}

/** Owns the settle lifecycle over an OpenFeature provider: context, cycles, timeout and notification. */
export class SystemFlagsClientEngine implements ISystemFlagsClient {
  private readonly _domain = crypto.randomUUID();
  private readonly _client: Client;
  private readonly _onChanged = new Event<void>();

  private _context: ResolvedSystemFlagsContext = EMPTY_CONTEXT;
  private _state: EngineState = 'idle';
  private _targetingKey: string | undefined;

  private _cycleContext: ResolvedSystemFlagsContext = EMPTY_CONTEXT;
  private _cycle: CycleState = 'waiting';
  private _pending: ManualPromise<SettleResult> = createManualPromise<SettleResult>();

  private _timer: ReturnType<typeof setTimeout> | undefined;

  private readonly _provider: Provider | undefined;
  private readonly _targetingKeyStore: ITargetingKeyStore;
  private readonly _timeoutMs: number;

  constructor(
    provider: Provider | undefined,
    targetingKeyStore: ITargetingKeyStore,
    timeoutMs: number,
  ) {
    this._provider = provider;
    this._targetingKeyStore = targetingKeyStore;
    this._timeoutMs = timeoutMs;
    this._client = OpenFeature.getClient(this._domain);
  }

  /**
   * Fires when a value `get` returns, or `isSettled`, may have changed.
   *
   * Triggered by:
   * - A provider event that settles or revises a cycle: Ready, Stale, Error, and ConfigurationChanged once the SDK can evaluate.
   * - A cycle being superseded.
   */
  get onChanged(): IEvent<void> {
    return this._onChanged.expose();
  }

  get isSettled(): boolean {
    return this._cycle !== 'waiting';
  }

  /** Handlers are attached and cycles are bounded by a timer only while a started client holds a provider. */
  private get hasProvider(): boolean {
    return this._state === 'started' && this._provider !== undefined;
  }

  /**
   * The flag's current value, synchronously; never throws.
   * Resolves to the provider's value while it holds a snapshot for the current context, and to `flag.defaultValue` when it does not.
   */
  get<F extends FlagDefinition>(flag: F): FlagValueOf<F> {
    if (this._cycle !== 'ended-with-snapshot') {
      return flag.defaultValue as FlagValueOf<F>;
    }
    return this.evaluate(flag) as FlagValueOf<F>;
  }

  setContext(context: SystemFlagsContext): void {
    if (this._state === 'disposed') {
      return;
    }

    const next = resolveContext(context);
    if (deepEqual(next, this._context)) {
      return;
    }
    this._context = next;

    if (this._state !== 'started') {
      return;
    }

    if (!this.hasProvider) {
      this.settleWithoutProvider(this._context);
      return;
    }

    this.pushContext();
  }

  /**
   * Sends the current context to the provider again, so it fetches the flags for it once more.
   *
   * Effect:
   * - Starts no settle cycle: the provider's events end or revise the current one.
   * - Does nothing unless a provider is attached.
   */
  resendContext(): void {
    if (this.hasProvider) {
      this.sendContext();
    }
  }

  start(): void {
    if (this._state !== 'idle') {
      return;
    }
    this._state = 'started';

    if (!this._provider) {
      this.settleWithoutProvider(this._context);
      return;
    }

    this.attachHandlers();
    this.beginCycle(this._context);

    void OpenFeature.setProviderAndWait(this._domain, this._provider, this.buildContext()).catch((err: unknown) => {
      logger.log('Provider failed to initialize — flags stay on defaults; a later event settles reactively.', err);
    });
  }

  whenSettled(): Promise<SettleResult> {
    return this._pending.promise;
  }

  dispose(): void {
    const wasAttached = this.hasProvider;
    this._state = 'disposed';

    if (wasAttached) {
      this._client.removeHandler(ProviderEvents.Ready, this.onReady);
      this._client.removeHandler(ProviderEvents.ConfigurationChanged, this.onConfigurationChanged);
      this._client.removeHandler(ProviderEvents.Stale, this.onStale);
      this._client.removeHandler(ProviderEvents.Error, this.onError);
    }
    if (this._timer !== undefined) {
      clearTimeout(this._timer);
      this._timer = undefined;
    }
    this._onChanged.resetHandlers();
    // A waiter outlives the client otherwise.
    this._pending.resolve({ context: this._cycleContext, reason: 'stale' });
  }

  // Non-generic switch: the union discriminant narrows `defaultValue` per branch, so no casts are needed.
  private evaluate(flag: FlagDefinition): JsonFlagValue {
    switch (flag.type) {
      case 'boolean':
        return this._client.getBooleanValue(flag.key, flag.defaultValue);
      case 'string':
        return this._client.getStringValue(flag.key, flag.defaultValue);
      case 'number':
        return this._client.getNumberValue(flag.key, flag.defaultValue);
      case 'object':
        // getObjectValue is constrained to JsonValue — object flags must be JSON-serializable.
        return this._client.getObjectValue(flag.key, flag.defaultValue as JsonValue) as JsonFlagValue;
    }
  }

  private attachHandlers(): void {
    this._client.addHandler(ProviderEvents.Ready, this.onReady);
    this._client.addHandler(ProviderEvents.ConfigurationChanged, this.onConfigurationChanged);
    this._client.addHandler(ProviderEvents.Stale, this.onStale);
    this._client.addHandler(ProviderEvents.Error, this.onError);
  }

  private readonly onReady = (): void => this.resolveCycle('ready');

  private readonly onConfigurationChanged = (): void => {
    // The SDK cannot evaluate while NOT_READY, and Ready follows.
    if (this._client.providerStatus !== ProviderStatus.NOT_READY) {
      this.resolveCycle('ready');
    }
  };

  private readonly onStale = (): void => this.resolveCycle('stale');

  private readonly onError = (eventDetails?: { message?: string }): void => {
    this.resolveCycle('error');
    logger.log('Provider error', eventDetails?.message ?? '(no message)');
  };

  private pushContext(): void {
    this.beginCycle(this._context);
    this.sendContext();
  }

  private sendContext(): void {
    void OpenFeature.setContext(this._domain, this.buildContext()).catch((err: unknown) => {
      logger.log('setContext failed — flags stay on the previous snapshot.', err);
    });
  }

  private buildContext(): EvaluationContext {
    const context: EvaluationContext = { targetingKey: this.resolveTargetingKey() };
    for (const [name, value] of Object.entries(this._context)) {
      if (value !== null) {
        context[name] = value;
      }
    }
    return context;
  }

  private resolveTargetingKey(): string {
    this._targetingKey ??= resolveTargetingKey(this._targetingKeyStore);
    return this._targetingKey;
  }

  /**
   * Opens and ends a cycle in one step, for a context no provider will ever evaluate.
   * Subscribers hear the settle once, since the cycle never waits.
   * No timer can be pending here: a cycle is only ever bounded while a provider exists.
   */
  private settleWithoutProvider(context: ResolvedSystemFlagsContext): void {
    if (this._cycle !== 'waiting') {
      this._pending = createManualPromise<SettleResult>();
    }

    this._cycleContext = context;
    this._cycle = cycleStateFor('no-provider');
    this._pending.resolve({ context, reason: 'no-provider' });
    this._onChanged.trigger();
  }

  /**
   * Starts a new settle cycle.
   * Rearms the timeout while a provider exists.
   *
   * Supersession:
   * - A superseded cycle stops counting as settled.
   * - Its `whenSettled()` waiters carry over and resolve with whichever cycle settles next.
   */
  private beginCycle(context: ResolvedSystemFlagsContext): void {
    const wasSettled = this._cycle !== 'waiting';

    if (this._timer !== undefined) {
      clearTimeout(this._timer);
      this._timer = undefined;
    }

    if (wasSettled) {
      this._pending = createManualPromise<SettleResult>();
    }
    this._cycle = 'waiting';
    this._cycleContext = context;

    if (this.hasProvider) {
      this._timer = setTimeout(() => {
        this._timer = undefined;
        this.resolveCycle('timeout');
      }, this._timeoutMs);
    }

    if (wasSettled) {
      this._onChanged.trigger();
    }
  }

  /**
   * Applies a settle event's outcome to the current cycle, and notifies.
   *
   * Ending vs. revising:
   * - A waiting cycle ends: the timer clears and `_pending` resolves.
   * - An ended cycle is revised: only the cycle state changes, so `whenSettled()` keeps reporting why the wait first ended.
   */
  private resolveCycle(reason: SettleReason): void {
    const wasWaiting = this._cycle === 'waiting';

    if (wasWaiting && this._timer !== undefined) {
      clearTimeout(this._timer);
      this._timer = undefined;
    }

    this._cycle = cycleStateFor(reason);

    if (wasWaiting) {
      this._pending.resolve({ context: this._cycleContext, reason });
    }

    this._onChanged.trigger();
  }
}

/**
 * `ready` and `stale` leave the provider holding a snapshot for the cycle's context.
 * Every other reason does not.
 */
function cycleStateFor(reason: SettleReason): CycleState {
  return reason === 'ready' || reason === 'stale' ? 'ended-with-snapshot' : 'ended-without-snapshot';
}
